import {DateValue, parseAbsolute} from '@internationalized/date';
import {Fragment, memo} from 'react';
import {useDateFormatter} from '@ui/i18n/use-date-formatter';
import {shallowEqual} from '@ui/utils/shallow-equal';
import {useSettings} from '@ui/settings/use-settings';
import {useUserTimezone} from '@ui/i18n/use-user-timezone';
import { useBootstrapDataStore } from '@ui/bootstrap-data/bootstrap-data-store';

export const DateFormatPresets: Record<
  'numeric' | 'short' | 'long' | 'timestamp' | 'time',
  Intl.DateTimeFormatOptions
> = {
  numeric: {year: 'numeric', month: '2-digit', day: '2-digit'},
  short: {year: 'numeric', month: 'short', day: '2-digit'},
  long: {month: 'long', day: '2-digit', year: 'numeric'},
  timestamp: {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  },
  time: {
    hour: '2-digit',
    minute: '2-digit',
  },
};

interface FormattedDateProps {
  date?: string | DateValue | Date;
  options?: Intl.DateTimeFormatOptions;
  preset?: keyof typeof DateFormatPresets;
  timezone?: string;
}

export const FormattedDate = memo(
  ({date, options, preset, timezone: propsTimezone}: FormattedDateProps) => {
    const {dates} = useSettings();
    const data = useBootstrapDataStore(s => s.data);
    const userTimezone = useUserTimezone();
    const timezone = propsTimezone || options?.timeZone || userTimezone;

    const locale = navigator.language; // ou venir du context i18n si dispo

    const formatter = useDateFormatter(
      options ||
        (DateFormatPresets as Record<string, Intl.DateTimeFormatOptions>)[
          preset || dates?.format
        ],
    );

    if (!date) {
      return null;
    }

    try {
      if (typeof date === 'string') {
        date = parseAbsolute(date, timezone).toDate();
      } else if ('toDate' in date) {
        date = date.toDate(timezone);
      }
    } catch (e) {
      return null;
    }
    
    // 🟡 Format corse si locale = 'co' ou 'co-FR'
    if (data.i18n.language === 'co') {
      return <Fragment>{formatCorsicanDate(date as Date)}</Fragment>;
    }

    return <Fragment>{formatter.format(date as Date)}</Fragment>;
  },
  shallowEqual,
);

// 🔤 Formatter corse
const corsicanMonthsShort = [
  'ghj.', 'fer.', 'mar.', 'apr.', 'mag.', 'ghj.', 'lug.', 'aos.',
  'set.', 'ott.', 'nuv.', 'dic.',
];
const corsicanMonthsLong = [
  'ghjennaghju', 'ferraghju', 'marzu', 'aprile', 'maghju', 'ghjugnu',
  'lugliu', 'aostu', 'settembre', 'ottobre', 'nuvembre', 'dicembre',
];

function formatCorsicanDate(date: Date): string {
  const day = date.getDate();
  const month = corsicanMonthsLong[date.getMonth()];
  const year = date.getFullYear();
  return `u ${day} di ${month} di u ${year}`;
}

function formatCorsicanRange(start: Date, end: Date): string {
  const sameDay = isSameDay(start, end);
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const sameYear = start.getFullYear() === end.getFullYear();

  const p1Day = start.getDate();
  const p2Day = end.getDate();
  const m1 = corsicanMonthsLong[start.getMonth()];
  const m2 = corsicanMonthsLong[end.getMonth()];
  const y1 = start.getFullYear();
  const y2 = end.getFullYear();

  const diffDays = Math.round((+end - +start) / 86_400_000);

  if (sameDay) {
    return formatCorsicanDate(start);
  }

  if (sameMonth && diffDays === 1) {
    // i 10 è 11 di lugliu di u 2025
    return `i ${p1Day} è ${p2Day} di ${m1} di u ${y1}`;
  }

  if (sameMonth) {
    // da u 10 à u 20 di lugliu di u 2025
    return `da u ${p1Day} à u ${p2Day} di ${m1} di u ${y1}`;
  }

  if (sameYear) {
    // da u 28 di ghjugnu à u 2 di lugliu di u 2025
    return `da u ${p1Day} di ${m1} à u ${p2Day} di ${m2} di u ${y1}`;
  }

  // da u 28 di dicembre di u 2025 à u 2 di ghjennaghju di u 2026
  return `da u ${p1Day} di ${m1} di u ${y1} à u ${p2Day} di ${m2} di u ${y2}`;
}


/**
 * Helper to convert all supported inputs to a JS Date in the correct TZ.
 */
function normalizeDate(
  value: string | Date | DateValue,
  timezone: string,
): Date | null {
  try {
    if (!value) return null;
    if (typeof value === 'string') {
      return parseAbsolute(value, timezone).toDate();
    }
    if (value instanceof Date) {
      return value;
    }
    // DateValue (from react-aria) exposes toDate()
    if ('toDate' in value) {
      // @ts-ignore
      return (value as DateValue).toDate(timezone);
    }
  } catch (_) {
    /* ignored */
  }
  return null;
}

/**
 * Return day, month(long), year & month(short) for string building.
 */
function getDateParts(date: Date, locale: string) {
  const day = date.getDate();
  const year = date.getFullYear();
  const monthLong = new Intl.DateTimeFormat(locale, {month: 'long'}).format(date);
  const monthShort = new Intl.DateTimeFormat(locale, {month: 'short'}).format(date);
  return {day, monthLong, monthShort, year};
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Formatte une plage de dates selon la locale :
 *   – preset  "long"  : rendu existant (par défaut)
 *   – preset  "short" : version courte
 *   – showYear        : affiche l’année (true par défaut)
 */
export function formatDateRange(
  start: Date,
  end: Date,
  locale: string,
  preset: 'long' | 'short' = 'long',
  showYear = true,
): string {
  // Toujours du plus petit au plus grand
  if (start > end) [start, end] = [end, start];

  const localePrefix = locale.split('-')[0];

  const sameDay   = isSameDay(start, end);
  const sameMonth = start.getMonth() === end.getMonth() &&
                    start.getFullYear() === end.getFullYear();
  const sameYear  = start.getFullYear() === end.getFullYear();
  const diffDays  = Math.round((+end - +start) / 86_400_000); // nb jours

  /* ----------------------------------------------------------------
   *   PRESET « SHORT »
   * ---------------------------------------------------------------- */
  if (preset === 'short') {
    /* ---------- Corse ---------- */
    if (localePrefix === 'co') {
      const d1 = start.getDate();
      const d2 = end.getDate();
      const m1 = corsicanMonthsShort[start.getMonth()];
      const m2 = corsicanMonthsShort[end.getMonth()];
      const y1 = start.getFullYear();
      const y2 = end.getFullYear();

      if (sameDay)       return `${d1} di ${m1}${showYear ? ` ${y1}` : ''}`;
      if (sameMonth)     return `${d1}-${d2} di ${m1}${showYear ? ` ${y1}` : ''}`;
      if (sameYear)      return `${d1} di ${m1} - ${d2} di ${m2}${showYear ? ` ${y1}` : ''}`;
      return `${d1} di ${m1} ${y1} - ${d2} di ${m2} ${y2}`;
    }

    /* ---------- Français ---------- */
    if (localePrefix === 'fr') {
      const p1 = getDateParts(start, 'fr');
      const p2 = getDateParts(end, 'fr');

      if (sameDay)       return `${p1.day} ${p1.monthShort}${showYear ? ` ${p1.year}` : ''}`;
      if (sameMonth)     return `${p1.day}-${p2.day} ${p1.monthShort}${showYear ? ` ${p1.year}` : ''}`;
      if (sameYear)      return `${p1.day} ${p1.monthShort} - ${p2.day} ${p2.monthShort}${showYear ? ` ${p1.year}` : ''}`;
      return `${p1.day} ${p1.monthShort} ${p1.year} - ${p2.day} ${p2.monthShort} ${p2.year}`;
    }

    /* ---------- Anglais / défaut ---------- */
    {
      const p1 = getDateParts(start, locale); // locale héritée (en, es, …)
      const p2 = getDateParts(end, locale);

      if (sameDay)       return `${p1.monthShort} ${p1.day}${showYear ? ` ${p1.year}` : ''}`;
      if (sameMonth)     return `${p1.monthShort} ${p1.day}-${p2.day}${showYear ? `, ${p1.year}` : ''}`;
      if (sameYear)      return `${p1.monthShort} ${p1.day} - ${p2.monthShort} ${p2.day}${showYear ? `, ${p1.year}` : ''}`;
      return `${p1.monthShort} ${p1.day}, ${p1.year} - ${p2.monthShort} ${p2.day}, ${p2.year}`;
    }
  }

  /* ----------------------------------------------------------------
   *   PRESET « LONG » (comportement existant)
   * ---------------------------------------------------------------- */

  /* ---------- Corse ---------- */
  if (localePrefix === 'co') {
    return formatCorsicanRange(start, end);  // fonction d’origine inchangée
  }

  /* ---------- Français ---------- */
  if (localePrefix === 'fr') {
    const p1 = getDateParts(start, 'fr');
    const p2 = getDateParts(end, 'fr');

    if (sameDay)       return `Le ${p1.day} ${p1.monthLong} ${p1.year}`;
    if (sameMonth && diffDays === 1)
                       return `Les ${p1.day} et ${p2.day} ${p1.monthLong} ${p1.year}`;
    if (sameMonth)     return `Du ${p1.day} au ${p2.day} ${p1.monthLong} ${p1.year}`;
    if (sameYear)      return `Du ${p1.day} ${p1.monthLong} au ${p2.day} ${p2.monthLong} ${p1.year}`;
    return              `Du ${p1.day} ${p1.monthLong} ${p1.year} au ` +
                        `${p2.day} ${p2.monthLong} ${p2.year}`;
  }

  /* ---------- Anglais / défaut ---------- */
  {
    const p1 = getDateParts(start, locale);
    const p2 = getDateParts(end, locale);

    const monthDay = (p: ReturnType<typeof getDateParts>) => `${p.monthLong} ${p.day}`;

    if (sameDay)       return `${monthDay(p1)}, ${p1.year}`;
    if (sameMonth && diffDays === 1)
                       return `${p1.monthLong} ${p1.day} and ${p2.day}, ${p1.year}`;
    if (sameMonth)     return `${p1.monthLong} ${p1.day}-${p2.day}, ${p1.year}`;
    if (sameYear)      return `${p1.monthLong} ${p1.day} – ${p2.monthLong} ${p2.day}, ${p1.year}`;
    return              `${p1.monthShort} ${p1.day}, ${p1.year} – ` +
                        `${p2.monthShort} ${p2.day}, ${p2.year}`;
  }
}



interface FormattedDateRangeProps {
  start?: string | DateValue | Date;
  end?: string | DateValue | Date;
  timezone?: string;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
  /** 'long' (défaut) ou 'short' */
  preset?: 'long' | 'short';
  /** Afficher l’année ? (Défaut : true) */
  showYear?: boolean;
}

export const FormattedDateRange = memo(
  ({
    start,
    end,
    timezone: propsTz,
    className,
    as = 'span',
    preset = 'long',
    showYear = true,
  }: FormattedDateRangeProps) => {
    const userTz = useUserTimezone();
    const data = useBootstrapDataStore(s => s.data);

    const timezone = propsTz || userTz;
    const locale = data.i18n?.language || navigator.language;

    const startDate = normalizeDate(start as any, timezone);
    const endDate   = normalizeDate(end as any, timezone);

    if (!startDate || !endDate) return null;

    const text = formatDateRange(startDate, endDate, locale, preset, showYear);

    const Component: any = as;
    return <Component className={className}>{text}</Component>;
  },
  shallowEqual,
);


export default FormattedDateRange;

